import styles from './track-info-popover.module.css'

import { LuArrowRight } from '@react-icons/all-files/lu/LuArrowRight'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import { LuSettings } from '@react-icons/all-files/lu/LuSettings'
import * as React from 'react'
import { Link } from 'react-router-dom'

import {
  getCustomFieldsConfig,
  getMultiLevelSetting,
} from '@disco/state-management/selectors/user'
import { getNounForExt } from '@disco/utils/file-utils'

import type { Track } from 'types/track'

import { getTrackField } from 'utils/track-metadata'

import { Button, Flex, IconButton, Popover, Text, Tooltip } from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import { fetchTrackMetadata } from './fetch-track-metadata'

// Comment fields render multi-row content in the legacy tooltip; this popover
// sticks to single-value rows so it stays scannable at a glance.
const EXCLUDED_FIELDS = ['Comments', 'Client comments', 'Internal comments']

const FIELD_LABELS: Record<string, string> = {
  Formats: 'Format',
  Length: 'Duration',
}

const TrackInfoPopoverContent = ({
  track,
  customEditMetadataUrl,
  onNavigate,
}: {
  track: Track
  customEditMetadataUrl?: string | null | undefined
  onNavigate: () => void
}): React.ReactNode => {
  const metadataSubset: string[] = useSelector(
    state =>
      getMultiLevelSetting.explorer_trackhover_fields(state.user).settingUser,
  )
  const customFieldsConfig = useSelector(getCustomFieldsConfig)
  const [trackWithMetadata, setTrackWithMetadata] =
    React.useState<Track | null>(null)

  React.useEffect(() => {
    let cancelled = false
    fetchTrackMetadata(track).then(fetched => {
      if (!cancelled) setTrackWithMetadata(fetched)
    })
    return () => {
      cancelled = true
    }
  }, [track])

  const fullTrack = trackWithMetadata ?? track
  const configuredFields = metadataSubset.filter(
    prop => !EXCLUDED_FIELDS.includes(prop),
  )
  const fields = configuredFields
    .map(prop => ({
      prop,
      label: FIELD_LABELS[prop] ?? prop,
      value: getTrackField(fullTrack, prop, customFieldsConfig),
    }))
    .filter(({ value }) => typeof value === 'string' && value !== '')
  return (
    <Flex direction="column" gap="4" minHeight="0">
      {fields.length > 0 ? (
        <div className={styles.scrollArea}>
          <dl className={styles.metaGrid}>
            {fields.map(({ prop, label, value }) => (
              <React.Fragment key={prop}>
                <Text asChild size="2" color="gray">
                  <dt>{label}</dt>
                </Text>
                <Text asChild size="2">
                  <dd>{value}</dd>
                </Text>
              </React.Fragment>
            ))}
          </dl>
        </div>
      ) : configuredFields.length === 0 ? (
        <Text size="2" color="gray">
          No info fields selected. Choose fields in display settings.
        </Text>
      ) : (
        // Only claim there's no info once the fetch has settled, so the
        // message can't flash while values are still arriving.
        trackWithMetadata !== null && (
          <Text size="2" color="gray">
            No info available for this track.
          </Text>
        )
      )}
      <Flex gap="2">
        <Button asChild variant="soft" size="2" className={styles.cta}>
          <Link
            to={
              customEditMetadataUrl ||
              `/explorer/tracks/${track.id}/edit-metadata`
            }
            onClick={onNavigate}
          >
            View full {getNounForExt(track.ext)} info
            <LuArrowRight size={16} />
          </Link>
        </Button>
        <Tooltip content="Metadata display settings">
          <IconButton asChild variant="soft" size="2">
            <Link
              to="/settings/display"
              aria-label="Metadata display settings"
              onClick={onNavigate}
            >
              <LuSettings size={16} />
            </Link>
          </IconButton>
        </Tooltip>
      </Flex>
    </Flex>
  )
}

type Props = {
  track: Track
  customEditMetadataUrl?: string | null | undefined
}

/** Renders the (i) icon that opens the track's metadata (BPM, format, tags, etc.) on click. */
const TrackInfoPopover = ({
  track,
  customEditMetadataUrl,
}: Props): React.ReactNode => {
  const [open, setOpen] = React.useState(false)

  if (!track.uploaded || track.transcode_pending) return null

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <IconButton
          variant="ghost"
          color="gray"
          size="1"
          aria-label="Track info"
        >
          <LuInfo size={20} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content side="bottom" align="start" className={styles.content}>
        <TrackInfoPopoverContent
          track={track}
          customEditMetadataUrl={customEditMetadataUrl}
          onNavigate={() => setOpen(false)}
        />
      </Popover.Content>
    </Popover.Root>
  )
}

export default TrackInfoPopover
